import useRequest from "@/lib/request/useRequest";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { Pump } from "@/types/pump";
import { useMemo, useState } from "react";
import { getPumpName } from "@/lib/utils/pump";
import { Button, Card, CardBody, Input } from "@heroui/react";
import { faPencil, faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PumpForm from "./PumpForm";
import { useTranslation } from "react-i18next";

function ChemigationSystems() {
    const { t } = useTranslation();
    const { useGetRequest } = useRequest();
    const { fieldQuery } = useFieldDashboard() ?? {};

    const [searchText, setSearchText] = useState("");

    const [isEditing, setIsEditing] = useState(false);
    const [editingPump, setEditingPump] = useState<Pump | undefined>(undefined);

    const pumpsQuery = useGetRequest<Pump[]>({
        url: `pumps/`,
    });

    const pumps = useMemo(() => {
        return (
            pumpsQuery.data
                ?.filter((pump) => pump.grower == fieldQuery?.data?.grower)
                ?.filter((pump, index) => {
                    if (!searchText) return true;
                    return getPumpName(pump, index)
                        .toLowerCase()
                        .includes(searchText.toLowerCase());
                }) ?? []
        );
    }, [pumpsQuery.data, fieldQuery?.data?.grower, searchText]);

    return (
        <div className="flex flex-col gap-2">
            {isEditing ? (
                <PumpForm
                    editingPump={editingPump}
                    onFinish={() => {
                        setIsEditing(false);
                        setEditingPump(undefined);
                    }}
                />
            ) : (
                <>
                    <div className="flex flex-row gap-2 items-center justify-between">
                        <div>
                            <Input
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                size="sm"
                                placeholder={t(
                                    "chemigation_systems.search_pumps",
                                    {
                                        ns: "setup",
                                    },
                                )}
                                startContent={
                                    <FontAwesomeIcon icon={faSearch} />
                                }
                            />
                        </div>
                        <div>
                            <Button
                                size="sm"
                                color="primary"
                                startContent={<FontAwesomeIcon icon={faPlus} />}
                                onPress={() => {
                                    setIsEditing(true);
                                    setEditingPump(undefined);
                                }}
                            >
                                {t("chemigation_systems.add_pump", {
                                    ns: "setup",
                                })}
                            </Button>
                        </div>
                    </div>

                    {pumps?.map((pump, index) => (
                        <Card key={pump.id}>
                            <CardBody>
                                <div className="flex flex-row gap-2 items-center justify-between">
                                    <div>{getPumpName(pump, index)}</div>
                                    <div>
                                        <Button
                                            size="sm"
                                            startContent={
                                                <FontAwesomeIcon
                                                    icon={faPencil}
                                                />
                                            }
                                            onPress={() => {
                                                setEditingPump(pump);
                                                setIsEditing(true);
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    ))}
                    {!pumps.length && (
                        <div className="flex flex-row gap-2 items-center justify-center">
                            <div className="text-sm text-gray-500">
                                {t("chemigation_systems.no_pumps_found", {
                                    ns: "setup",
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default ChemigationSystems;
